import {isEmpty} from 'lodash-es';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {
  Button,
  CheckBox,
  CountryPicker,
  DatePicker,
  ImageInput,
  Input,
  Select,
  SpinningLoader,
} from '@/components';
import {NumberInput, TextArea} from '@/components/common/Input';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import {
  getRewardTierOptions,
  getRewardTypeOptions,
} from '@/constants/formatters';
import {useCountryList} from '@/context/CountryListContext';
import type {AdminAfriexReward, AfriexCountryCode} from '@/types';
import {AfriexRewardTier, AfriexRewardType, CountryStatus} from '@/types';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {generateRandomId} from '@/utils/generateRandomId';

interface UpdateRewardModalProps extends Pick<
  UserModalProps,
  'isOpen' | 'onClose'
> {
  data?: AdminAfriexReward;
  handleSave: (body: Partial<AdminAfriexReward>) => Promise<void>;
}

const UpdateRewardModal = ({
  isOpen,
  onClose,
  handleSave,
  data,
}: UpdateRewardModalProps) => {
  const {state: countryListState} = useCountryList();
  const {countryList} = countryListState;
  const [formValues, setFormValues] = useState<AdminAfriexReward>({
    isDeactivated: true,
  } as AdminAfriexReward);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isImageAdded, setIsImageAdded] = useState<boolean>(false);
  const imageId = useMemo(() => generateRandomId(), []);
  const defaultFileName = 'reward_card_image_' + imageId;
  const [fileName, setFileName] = useState<string>('');
  const [supportedCountries, setSupportedCountries] = useState<
    AfriexCountryCode[]
  >([]);

  const expiryDate = formValues?.ttl ? new Date(formValues.ttl) : undefined;

  const addCountryToList = (countryCode: AfriexCountryCode) => {
    if (!supportedCountries.includes(countryCode)) {
      setSupportedCountries([...supportedCountries, countryCode]);
    }
  };

  const removeFromCountryList = (countryCode: AfriexCountryCode) => {
    if (supportedCountries.includes(countryCode)) {
      setSupportedCountries(
        supportedCountries.filter((x) => x !== countryCode),
      );
    }
  };

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    const body = {
      ...formValues,
      type: formValues?.type || AfriexRewardType.GiftCard,
      tier: formValues?.tier || AfriexRewardTier.Member,
      supportedCountries,
      imageLink: isImageAdded ? fileName : formValues?.imageLink || '',
      pointsValue:
        Number(formValues?.pointsValue) || formValues?.pointsValue || 0,
      isDeactivated: formValues?.isDeactivated || false,
    };
    await handleSave(body);
    setIsLoading(false);
  }, [supportedCountries, formValues, handleSave, fileName]);

  useEffect(() => {
    if (!isEmpty(data)) {
      setFormValues(data);
      setSupportedCountries(data?.supportedCountries ?? []);
    }
    setFileName(data?.imageLink || defaultFileName);
  }, [data, defaultFileName]);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormValues((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-11/12 md:w-5/12 lg:w-4/12 xl:w-3/12 px-4 sm:px-0"
      height="h-4/5"
      closeOnOutsideClick
      title="Add New Reward"
    >
      {isLoading ? (
        <div className="flex w-full h-full justify-center items-center my-5">
          <SpinningLoader />
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} className="flex flex-col space-y-5">
            <div className="mt-5">
              <Input
                name="name"
                label="Name"
                value={formValues?.name || ''}
                className="w-full"
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="mt-5">
              <TextArea
                name="description"
                label="Description"
                type="text"
                value={formValues?.description || ''}
                className="w-full"
                placeholder="Enter Description"
                onChange={handleInputChange}
                aria-rowspan={4}
                required
              />
            </div>

            <div className="mt-5">
              <Select
                name="type"
                label="Select Reward Type"
                onChange={handleInputChange}
                value={formValues?.type || ''}
                options={getRewardTypeOptions()}
                placeholder="Select Type"
                required
              />
            </div>

            <div className="mt-5">
              <Select
                name="tier"
                label="Lowest Eligible Tier"
                onChange={handleInputChange}
                value={formValues?.tier?.toLowerCase() || ''}
                options={getRewardTierOptions()}
                placeholder="Select Type"
                required
              />
            </div>

            <div className="mt-5">
              <DatePicker
                name="ttl"
                label="Expires On (Optional)"
                selectedDate={expiryDate}
                setSelectedDate={(date) =>
                  setFormValues({
                    ...formValues,
                    ttl: new Date(date)?.toISOString() || '',
                  })
                }
                placeholderText="Select Date"
              />
            </div>

            <ImageInput
              label="Upload Image"
              alt="Reward Item Image"
              name={fileName}
              value={formValues?.imageLink || ''}
              onChange={(value: string) => {
                setFormValues((x) => ({...x, imageLink: value || ''}));
                setIsImageAdded(true);
              }}
              required
            />

            <div className="mt-5">
              <NumberInput
                name="pointsValue"
                label="Points Value"
                min="0"
                step="1"
                value={formValues?.pointsValue || ''}
                className="w-full"
                onValueChange={(rawValue) =>
                  setFormValues((prev) => ({
                    ...prev,
                    pointsValue: Number(rawValue),
                  }))
                }
                required
              />
            </div>

            <div className="mt-5">
              {supportedCountries?.map((countryCode, index) => (
                <div
                  className="flex justify-between space-x-2 border-b-1 py-2"
                  key={index}
                >
                  <label>
                    {countryCode}{' '}
                    {mapCountryCodeToFlags(countryList)[countryCode]}
                  </label>
                  <Button
                    variant={'ghost'}
                    onClick={() => removeFromCountryList(countryCode)}
                  >
                    Remove
                  </Button>
                </div>
              ))}

              <CountryPicker
                label="Add Country Available"
                filter={{status: CountryStatus.ACTIVE}}
                value={
                  supportedCountries?.[supportedCountries?.length - 1] || ''
                }
                onChange={(evt: React.ChangeEvent<HTMLSelectElement>) =>
                  addCountryToList(
                    (evt.target.value ||
                      supportedCountries[0]) as AfriexCountryCode,
                  )
                }
              />
            </div>
            <div className="mt-5">
              <CheckBox
                label="Mark as active"
                type="checkbox"
                checked={!formValues?.isDeactivated}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormValues((x) => ({
                    ...x,
                    isDeactivated: !e.target?.checked,
                  }))
                }
              />
            </div>
            <div className="flex w-full justify-end space-x-2 py-5">
              <Button colorScheme="cyan" onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button
                disabled={isLoading}
                type="submit"
                variant="solid"
                colorScheme="cyan"
              >
                Submit
              </Button>
            </div>
          </form>
        </>
      )}
    </Modal>
  );
};

export default UpdateRewardModal;
